import { useState } from 'react';

import { MatchCard } from '../components/MatchCard';
import { Button, Card, FormError } from '../components/ui';
import { useAwaitingMe, useConfirmMatch, useDisputeMatch } from '../hooks/useMatches';

export function ConfirmationsPage() {
  const awaiting = useAwaitingMe();
  const confirm = useConfirmMatch();
  const dispute = useDisputeMatch();

  const [disputingId, setDisputingId] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(matchId: number) {
    setError(null);
    try {
      await confirm.mutateAsync(matchId);
    } catch {
      setError('Could not confirm that result. It may already have been resolved.');
    }
  }

  async function handleDispute(matchId: number) {
    setError(null);
    if (reason.trim().length === 0) {
      setError('Say briefly what was wrong.');
      return;
    }
    try {
      await dispute.mutateAsync({ matchId, reason: reason.trim() });
      setDisputingId(null);
      setReason('');
    } catch {
      setError('Could not raise that dispute.');
    }
  }

  if (awaiting.isPending) {
    return <p className="text-sm text-slate-500">Loading…</p>;
  }

  const matches = awaiting.data?.matches ?? [];

  return (
    <>
      <h1 className="mb-1 text-2xl font-bold tracking-tight">Awaiting your confirmation</h1>
      <p className="mb-5 text-sm text-slate-500 dark:text-slate-400">
        Results your opponents submitted. Ratings only change once you confirm.
      </p>

      <FormError message={error} />

      {matches.length === 0 ? (
        <Card>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Nothing to confirm right now.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {matches.map((match) => (
            <MatchCard key={match.id} match={match}>
              {disputingId === match.id ? (
                <div className="space-y-2">
                  <label htmlFor={`reason-${match.id}`} className="block text-sm font-medium">
                    What was wrong?
                  </label>
                  <input
                    id={`reason-${match.id}`}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={280}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
                    placeholder="e.g. that was 10-8 to us"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => void handleDispute(match.id)}
                      disabled={dispute.isPending}
                    >
                      Raise dispute
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setDisputingId(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={() => void handleConfirm(match.id)}
                    disabled={confirm.isPending}
                  >
                    Confirm
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setDisputingId(match.id)}>
                    Dispute
                  </Button>
                </div>
              )}
            </MatchCard>
          ))}
        </div>
      )}
    </>
  );
}
